/**
 * Rueckfrage beim Entfernen eines GEERBTEN Bestelldokuments (Spec „Dokument entfernen“, Fassung 2):
 * der Nutzer waehlt, ob das Dokument nur bei dieser Bestellung oder bei allen entfernt wird, die es
 * ueber Sendung, Bestellgruppe oder Sammelsendung teilen. Ein eigenes Dokument fragt dagegen nur per
 * `confirm` (siehe `DokumenteTab.tsx#loeschen`).
 *
 * Gleiches Baumuster und gleiche Klassen wie `ReichweiteDialog.tsx`/`ReichweiteAuswahl.tsx`
 * (`createPortal` nach `document.body`, `board-editor-*`, `.reichweite-auswahl`) - aber BEWUSST eine
 * eigene Komponente statt Wiederverwendung: die Upload-Auswahl liefert `bestellung`/`gruppe`/`sendung`,
 * schlaegt je Dokumenttyp eine Vorbelegung vor (`reichweiteVorgabe`, haeufig „Sendung“) und heisst
 * „Weiter“ - beim Entfernen gibt es nur `bestellung`/`alle`, und die sichere Voreinstellung ist immer
 * „Nur diese Bestellung“. Die Zahl `anzahl` nennt der Server in der Belegliste (`reichweiteAnzahl`).
 *
 * Ausnahme Sammelsendung: dort lehnt der Server `reichweite: "bestellung"` mit 422 ab (die Elternzeile
 * haengt das Dokument bei jedem Sammelsendungs-Upload wieder an). Der Dialog bietet dann nur „Alle“ an,
 * mit einem Hinweis, und ist damit vorbelegt.
 */
import { createPortal } from "react-dom";
import { useState } from "react";
import type { EntfernReichweite } from "../../../api/dokumente.ts";
import { Ikone } from "../../Ikone.tsx";

export interface EntfernenDialogProps {
  dateiname: string;
  /** Beschriftung des Dokumenttyps (`DocType.label`). */
  typLabel: string;
  herkunft: "sendung" | "gruppe" | "sammelsendung";
  /** Zahl der Bestellungen, die „Alle“ trifft. */
  anzahl: number;
  /** Sendungsnummer (bei `sendung`/`sammelsendung`); bei der Bestellgruppe unbenutzt. */
  sendungsNr?: string | null;
  onAbbrechen: () => void;
  onBestaetigen: (reichweite: EntfernReichweite) => void;
}

/** Der Text der Option „Alle“, z. B. „Alle 3 Bestellungen der Sendung DSV-9001“. */
export function alleBeschriftung(
  herkunft: EntfernenDialogProps["herkunft"],
  anzahl: number,
  sendungsNr?: string | null,
): string {
  // Bei einer einzigen Bestellung kein „Alle 1 Bestellungen“.
  const zahl = anzahl === 1 ? "Die einzige Bestellung" : `Alle ${anzahl} Bestellungen`;
  const nr = sendungsNr ? ` ${sendungsNr}` : "";
  if (herkunft === "gruppe") return `${zahl} der Bestellgruppe`;
  if (herkunft === "sammelsendung") return `${zahl} der Sammelsendung${nr}`;
  return `${zahl} der Sendung${nr}`;
}

export function EntfernenDialog({
  dateiname,
  typLabel,
  herkunft,
  anzahl,
  sendungsNr,
  onAbbrechen,
  onBestaetigen,
}: EntfernenDialogProps) {
  const nurAlle = herkunft === "sammelsendung";
  const [gewaehlt, setGewaehlt] = useState<EntfernReichweite>(nurAlle ? "alle" : "bestellung");

  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onAbbrechen();
      }}
    >
      <section
        className="board-editor-dialog reichweite-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="entfernenDialogTitel"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="entfernenDialogTitel">Dokument entfernen</h2>
            <p>
              „{dateiname}“ ({typLabel}) gilt auch für andere Bestellungen. Die Datei bleibt unter „Entfernte
              Dokumente“ abrufbar.
            </p>
          </div>
          <button className="icon-btn" type="button" onClick={onAbbrechen} title="Abbrechen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body">
          <fieldset className="reichweite-auswahl">
            <legend>Entfernen bei</legend>
            {nurAlle ? null : (
              <label>
                <input
                  type="radio"
                  name="entfernen-reichweite"
                  value="bestellung"
                  checked={gewaehlt === "bestellung"}
                  onChange={() => setGewaehlt("bestellung")}
                />
                Nur diese Bestellung
              </label>
            )}
            <label>
              <input
                type="radio"
                name="entfernen-reichweite"
                value="alle"
                checked={gewaehlt === "alle"}
                onChange={() => setGewaehlt("alle")}
              />
              {alleBeschriftung(herkunft, anzahl, sendungsNr)}
            </label>
          </fieldset>
          {nurAlle ? (
            <p>
              Dokumente der Sammelsendung gelten immer für alle Bestellungen der Sammelsendung.
            </p>
          ) : null}
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button className="btn-primary" type="button" onClick={() => onBestaetigen(gewaehlt)}>
            <Ikone name="trash-2" />
            Entfernen
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
